/**
 * D-EZ-102 / D-EZ-95 — TRACKS WITH HISTORY.
 *
 * Per racetrack (the physical venue, so every card year of Daytona shares one
 * history): past winners, the defending winner, who has won here most, the
 * biggest wrecks, and the track's records — all read off the league's archive
 * and the season being raced (`history/tracks.ts`). A track the league has not
 * visited says so plainly.
 */

import { useMemo, useState } from 'react';
import { allRaces, trackHistory, type HistorySeason, type TrackHistory } from '../../domain/history/index.ts';
import { TRACK_CARDS, type TrackCard } from '../../domain/turn.ts';
import { TrackMark } from '../RaceLogo.tsx';
import { ordinal } from '../../domain/story/raceStory.ts';

interface Venue {
  venue: string;
  card: TrackCard;
  history: TrackHistory;
}

/** One card per racetrack: the newest card of each venue speaks for it. */
function venues(): { venue: string; card: TrackCard }[] {
  const by = new Map<string, TrackCard>();
  for (const t of TRACK_CARDS) {
    const v = t.venue ?? t.id;
    const cur = by.get(v);
    if (!cur || (t.cardYear ?? 0) > (cur.cardYear ?? 0)) by.set(v, t);
  }
  return [...by.entries()].map(([venue, card]) => ({ venue, card }));
}

const trackName = (card: TrackCard): string => card.name.replace(/^\s*(19|20)\d{2}s?\s+/, '');

export default function TrackHistoryPanel({ seasons }: { seasons: readonly HistorySeason[] }) {
  const races = useMemo(() => allRaces(seasons), [seasons]);
  const all = useMemo<Venue[]>(
    () => venues().map(({ venue, card }) => ({ venue, card, history: trackHistory(races, venue) })),
    [races],
  );
  const raced = all.filter((v) => v.history.races > 0).sort((a, b) => b.history.races - a.history.races || trackName(a.card).localeCompare(trackName(b.card)));
  const notYet = all.filter((v) => v.history.races === 0).sort((a, b) => trackName(a.card).localeCompare(trackName(b.card)));
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="trkhist" aria-labelledby="trkhist-h">
      <header className="trkhist__head">
        <h2 id="trkhist-h">Track history</h2>
        <p className="trkhist__sub">
          {raced.length === 0
            ? 'No races here yet in this league. Race a season and every track keeps its own history — winners, wrecks and records.'
            : `${races.length} race${races.length === 1 ? '' : 's'} run at ${raced.length} track${raced.length === 1 ? '' : 's'} in this league.`}
        </p>
      </header>

      {raced.length > 0 && (
        <div className="trkhist__grid">
          {raced.map((v) => (
            <TrackStory key={v.venue} v={v} expanded={open === v.venue} onToggle={() => setOpen((o) => (o === v.venue ? null : v.venue))} />
          ))}
        </div>
      )}

      {notYet.length > 0 && raced.length > 0 && (
        <details className="trkhist__notyet">
          <summary>Not raced here yet ({notYet.length})</summary>
          <ul>
            {notYet.map((v) => (
              <li key={v.venue}>
                <TrackMark trackId={v.card.id} size={18} />
                {trackName(v.card)} — <span className="dim">No races here yet in this league.</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function TrackStory({ v, expanded, onToggle }: { v: Venue; expanded: boolean; onToggle: () => void }) {
  const h = v.history;
  const best = h.byDriver.find((d) => d.wins > 0) ?? null;
  const shownWinners = expanded ? h.winners : h.winners.slice(0, 4);
  const r = h.records;
  return (
    <article className="trkcard" aria-label={trackName(v.card)}>
      <header className="trkcard__head">
        <TrackMark trackId={v.card.id} size={34} />
        <div>
          <h3 className="trkcard__name">{trackName(v.card)}</h3>
          <p className="trkcard__meta">
            {v.card.category} · {h.races} race{h.races === 1 ? '' : 's'} in this league
          </p>
        </div>
      </header>

      <dl className="trkcard__facts">
        {h.defendingWinner && (
          <div>
            <dt>Defending winner</dt>
            <dd>
              {h.defendingWinner.name} <span className="dim">· season {h.defendingWinner.seasonNumber}, {h.defendingWinner.raceName}</span>
            </dd>
          </div>
        )}
        {best && (
          <div>
            <dt>Most wins here</dt>
            <dd>
              {best.name} <span className="dim">· {best.wins} win{best.wins === 1 ? '' : 's'} in {best.starts} start{best.starts === 1 ? '' : 's'}</span>
            </dd>
          </div>
        )}
        {r.winFromFurthestBack && (
          <div>
            <dt>Won from furthest back</dt>
            <dd>
              {r.winFromFurthestBack.name} <span className="dim">· from {ordinal(r.winFromFurthestBack.start)}, season {r.winFromFurthestBack.seasonNumber}</span>
            </dd>
          </div>
        )}
        {r.mostMilesLedInARace && r.mostMilesLedInARace.miles > 0 && (
          <div>
            <dt>Most miles led in a race</dt>
            <dd>
              {r.mostMilesLedInARace.name} <span className="dim">· {r.mostMilesLedInARace.miles} miles, season {r.mostMilesLedInARace.seasonNumber}</span>
            </dd>
          </div>
        )}
        {h.biggestWrecks[0] && (
          <div>
            <dt>Biggest wreck</dt>
            <dd>
              {h.biggestWrecks[0].cars} cars <span className="dim">· season {h.biggestWrecks[0].seasonNumber}, {h.biggestWrecks[0].raceName}</span>
            </dd>
          </div>
        )}
      </dl>

      <h4 className="trkcard__sub">Past winners</h4>
      <ol className="trkcard__winners">
        {shownWinners.map((w) => (
          <li key={`${w.seasonNumber}-${w.raceNumber}`}>
            <span className="trkcard__when">S{w.seasonNumber} · R{w.raceNumber}</span>
            <span className="trkcard__who">{w.name}</span>
            <span className="trkcard__race dim">{w.raceName}</span>
          </li>
        ))}
      </ol>
      {h.winners.length > 4 && (
        <button type="button" className="btn btn--quiet trkcard__more" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? 'Fewer' : `All ${h.winners.length} winners`}
        </button>
      )}
    </article>
  );
}
